import { approveStn, cancelStn, dispatchStn, type Stn } from '@api/warehouse-ops';
import useToaster from '@hooks/toaster/useToaster';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

type ActionableStn = Pick<Stn, 'source' | 'fromNodeId' | 'status' | 'saleOrderCode'>;

/**
 * Only an STN raised on the platform, leaving one of OUR warehouses, is ours to act on.
 * Externally sourced ones (Amazon SP-API, a WMS gate pass) are mirrored here read-only; their
 * lifecycle belongs to the source system.
 */
export const isPlatformStn = (stn: Pick<Stn, 'source' | 'fromNodeId'>): boolean =>
    stn.source === 'PLATFORM' && !!stn.fromNodeId;

/**
 * An STN raised from a sale order (a B2B customer dispatch). It is dispatched where it was raised,
 * on the order, with its transporter and invoice; and it is received by the buyer, not by us.
 */
export const isSaleOrderStn = (stn: Pick<Stn, 'saleOrderCode'>): boolean => !!stn.saleOrderCode;

export type StnNextStep = 'APPROVE' | 'DISPATCH' | 'RECEIVE';

/**
 * The one step that moves a transfer forward, or null when there is none for us to take.
 * `receivable` = the STN has a page it is received on (stnReceivePath): a CM site, a material
 * store or one of our warehouses. Anywhere else (a channel, a customer, a distributor) receives
 * it on their side, so there is no Receive here.
 */
export function stnNextStep(stn: ActionableStn, receivable: boolean): StnNextStep | null {
    if (!isPlatformStn(stn)) return null;
    switch (stn.status) {
        case 'DRAFT':
            return 'APPROVE';
        case 'APPROVED':
            return isSaleOrderStn(stn) ? null : 'DISPATCH';
        case 'DISPATCHED':
        case 'IN_TRANSIT':
            return receivable ? 'RECEIVE' : null;
        default:
            return null;
    }
}

const errText = (e: any): string => e?.error?.message || e?.message || 'Please retry.';

/**
 * Approve / dispatch / cancel for a stock transfer, shared by the Stock Transfers grid and the
 * STN side panel so a button behaves the same in both: one toast, and every view of the
 * transfer (panel, full page, grid, By SKU sheet) refreshed. Each mutation takes the STN id, so
 * a grid can tell which row is in flight from `variables`.
 */
export function useStnActions() {
    const queryClient = useQueryClient();
    const { showToastSuccess, showToastError } = useToaster();

    const refresh = useCallback(
        (stnId: string) => {
            void queryClient.invalidateQueries({ queryKey: ['stn-panel', stnId] });
            void queryClient.invalidateQueries({ queryKey: ['stn', stnId] });
            void queryClient.invalidateQueries({ queryKey: ['stns'] });
            void queryClient.invalidateQueries({ queryKey: ['stns-by-sku'] });
        },
        [queryClient],
    );

    const approve = useMutation({
        mutationFn: (stnId: string) => approveStn(stnId),
        onSuccess: (stn, stnId) => {
            showToastSuccess({ title: `${stn?.code ?? 'STN'} approved` });
            refresh(stnId);
        },
        onError: (e: any) => showToastError({ title: 'Could not approve', subtitle: errText(e) }),
    });
    const dispatch = useMutation({
        mutationFn: (stnId: string) => dispatchStn(stnId),
        onSuccess: (stn, stnId) => {
            showToastSuccess({ title: `${stn?.code ?? 'STN'} dispatched` });
            refresh(stnId);
        },
        onError: (e: any) => showToastError({ title: 'Could not dispatch', subtitle: errText(e) }),
    });
    const cancel = useMutation({
        mutationFn: (stnId: string) => cancelStn(stnId),
        onSuccess: (stn, stnId) => {
            showToastSuccess({ title: `${stn?.code ?? 'STN'} cancelled` });
            refresh(stnId);
        },
        onError: (e: any) => showToastError({ title: 'Could not cancel', subtitle: errText(e) }),
    });

    return { approve, dispatch, cancel, refresh, errText };
}
